import React from 'react';
import { GraduationCap } from 'lucide-react';

import type { CourseProfile } from '../../types/courseProfile';
import type {
  RubricCriterionCount,
  RubricDraft,
  RubricLevelCount,
  RubricPerRoleCriterionCount,
  RubricScope,
  RubricTransversalCriterionCount,
} from '../../types/rubricDraft';
import { expectedGroupCriterionTotal, RUBRIC_ROLE_ALL } from '../../types/rubricDraft';
import { buildRubricLevels, RUBRIC_LEVEL_COUNT_OPTIONS } from '../../constants/rubricLevelPresets';
import { EXAM_LEVEL_OPTIONS } from '../../constants/examAiParams';
import { NumberStepper } from './NumberStepper';
import {
  RUBRIC_CRITERION_COUNT_MAX,
  RUBRIC_CRITERION_COUNT_MIN,
} from '../../types/rubricDraft';

const CRITERION_COUNT_MIN = RUBRIC_CRITERION_COUNT_MIN;
const CRITERION_COUNT_MAX = RUBRIC_CRITERION_COUNT_MAX;
const TRANSVERSAL_COUNT_MIN = 0;
const TRANSVERSAL_COUNT_MAX = 4;
const PER_ROLE_COUNT_MIN = 1;
const PER_ROLE_COUNT_MAX = 5;

interface RubricChromeBarProps {
  draft: RubricDraft;
  course: CourseProfile;
  onChange: (draft: RubricDraft) => void;
}

function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ id: T; label: string; title?: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <div className="rubric-chrome__cluster">
      <span className="rubric-chrome__label">{label}</span>
      <div className="rubric-chrome__segment" role="group" aria-label={label}>
        {options.map((option) => {
          const active = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              title={option.title}
              aria-pressed={active}
              className={`rubric-chrome__seg${active ? ' rubric-chrome__seg--active' : ''}`}
              onClick={() => onChange(option.id)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RubricChromeBar({ draft, course, onChange }: RubricChromeBarProps) {
  const isGroup = draft.scope === 'group';
  const roleCount = draft.projectRoles?.length ?? 0;
  const groupTotal = expectedGroupCriterionTotal(draft);
  const courseLevelLabel =
    EXAM_LEVEL_OPTIONS.find((option) => option.id === course.level)?.label ?? course.level;
  const levelNames = draft.levels.map((level) => level.name).join(' · ');

  const handleScopeChange = (scope: RubricScope) => {
    if (scope === draft.scope) return;
    if (scope === 'individual') {
      onChange({
        ...draft,
        scope: 'individual',
        projectRoles: [],
        criteria: draft.criteria.map((criterion) => ({
          ...criterion,
          applicableRoleIds: [RUBRIC_ROLE_ALL],
        })),
      });
      return;
    }
    onChange({ ...draft, scope: 'group' });
  };

  const handleLevelCountChange = (count: RubricLevelCount) => {
    if (count === draft.levelCount) return;
    onChange({
      ...draft,
      levelCount: count,
      levels: buildRubricLevels(count),
      criteria: [],
      hasMatrix: false,
    });
  };

  return (
    <div className="rubric-chrome" role="toolbar" aria-label="Parámetros de la rúbrica">
      <div className="rubric-chrome__course" title="Contexto que usa la IA">
        <GraduationCap className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="rubric-chrome__course-name truncate">
            {course.displayName.trim() || 'Curso'}
          </p>
          <p className="rubric-chrome__course-meta truncate">
            {[courseLevelLabel, course.subjectTag].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      <div className="rubric-chrome__divider" aria-hidden />

      <SegmentedControl
        label="Tipo"
        value={(draft.scope ?? 'individual') as RubricScope}
        options={[
          { id: 'individual', label: 'Individual' },
          { id: 'group', label: 'Grupal' },
        ]}
        onChange={handleScopeChange}
      />

      <div className="rubric-chrome__divider" aria-hidden />

      <div className="rubric-chrome__cluster rubric-chrome__cluster--levels">
        <span className="rubric-chrome__label">Niveles de logro</span>
        <div className="rubric-chrome__segment" role="group" aria-label="Niveles de logro">
          {RUBRIC_LEVEL_COUNT_OPTIONS.map((option) => {
            const active = draft.levelCount === option.count;
            const preview = buildRubricLevels(option.count)
              .map((level) => level.name)
              .join(' · ');
            return (
              <button
                key={option.count}
                type="button"
                title={preview}
                aria-pressed={active}
                className={`rubric-chrome__seg${active ? ' rubric-chrome__seg--active' : ''}`}
                onClick={() => handleLevelCountChange(option.count)}
              >
                {option.count}
              </button>
            );
          })}
        </div>
        <p className="rubric-chrome__hint truncate" title={levelNames}>
          {levelNames}
        </p>
      </div>

      <div className="rubric-chrome__divider" aria-hidden />

      {isGroup ? (
        <>
          <NumberStepper
            label="Transversales"
            value={draft.transversalCriterionCount ?? 2}
            min={TRANSVERSAL_COUNT_MIN}
            max={TRANSVERSAL_COUNT_MAX}
            onChange={(next) =>
              onChange({
                ...draft,
                transversalCriterionCount: next as RubricTransversalCriterionCount,
              })
            }
            compact
          />
          <NumberStepper
            label="Por rol"
            value={draft.perRoleCriterionCount ?? 2}
            min={PER_ROLE_COUNT_MIN}
            max={PER_ROLE_COUNT_MAX}
            onChange={(next) =>
              onChange({
                ...draft,
                perRoleCriterionCount: next as RubricPerRoleCriterionCount,
              })
            }
            hint={
              roleCount > 0
                ? `Total IA: ${groupTotal}`
                : 'Roles en la matriz'
            }
            compact
          />
        </>
      ) : (
        <NumberStepper
          label="Criterios"
          value={draft.criterionCount ?? 4}
          min={CRITERION_COUNT_MIN}
          max={CRITERION_COUNT_MAX}
          onChange={(next) =>
            onChange({ ...draft, criterionCount: next as RubricCriterionCount })
          }
          hint="Cantidad para la IA"
          compact
        />
      )}
    </div>
  );
}
